import { useMemo } from "react";
import { motion } from "framer-motion";
import { usePrefersReducedMotion } from "@/hooks/useMediaQuery";

interface SparklesProps {
  count?: number;
  className?: string;
}

const RAINBOW_VARS = [
  "hsl(var(--rainbow-1))",
  "hsl(var(--rainbow-2))",
  "hsl(var(--rainbow-3))",
  "hsl(var(--rainbow-4))",
  "hsl(var(--rainbow-5))",
];

/**
 * Decorative sparkle particles that float around their parent.
 * Disabled when prefers-reduced-motion is set.
 */
export function Sparkles({ count = 10, className = "" }: SparklesProps) {
  const reduced = usePrefersReducedMotion();

  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: 3 + Math.random() * 5,
        delay: Math.random() * 3,
        duration: 2.4 + Math.random() * 2.6,
        color: RAINBOW_VARS[i % RAINBOW_VARS.length],
      })),
    [count],
  );

  if (reduced) return null;

  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-visible ${className}`}
    >
      {particles.map((p) => (
        <motion.span
          key={p.id}
          className="absolute rounded-full"
          style={{
            top: `${p.top}%`,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
          }}
          initial={{ scale: 0, opacity: 0, rotate: 0 }}
          animate={{
            scale: [0, 1, 0],
            opacity: [0, 0.9, 0],
            rotate: [0, 90, 180],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </span>
  );
}
